<template>
  <div class="otp-wrapper">
    <b-loading :is-full-page="true" :active="loading"></b-loading>

    <div class="box otp-card">
      <!-- Logo -->
      <div class="image-wrap">
        <img src="/images/TC wired for Gant Travel.png" alt="TripCircuit" />
      </div>

      <!-- Check icon -->
      <div class="icon-wrap">
        <span class="check-circle">
          <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
            <path d="M5 11l5 5L17 7" stroke="#3B6D11" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </span>
      </div>

      <h2 class="otp-title">Check your email</h2>
      <p class="otp-subtitle">
        We sent a 6-digit code to<br />
        <strong>{{ email }}</strong>
      </p>

      <!-- 6-digit OTP boxes -->
      <div class="otp-boxes" role="group" aria-label="One-time passcode">
        <input
          v-for="(digit, idx) in digits"
          :key="idx"
          :ref="`otpInput${idx}`"
          v-model="digits[idx]"
          type="text"
          inputmode="numeric"
          maxlength="1"
          class="otp-box"
          :class="{ filled: digit !== '' }"
          autocomplete="one-time-code"
          @input="onInput(idx, $event)"
          @keydown="onKeydown(idx, $event)"
          @paste="onPaste"
        />
      </div>

      <!-- Inline error -->
      <p v-if="errorMessage" class="otp-error">{{ errorMessage }}</p>

      <!-- Verify button (visible once all 6 digits entered) -->
      <b-button
        v-if="isComplete"
        type="is-gant"
        class="ubuntu mt-3"
        expanded
        :loading="loading"
        @click="verifyOtp"
      >
        Verify
      </b-button>

      <!-- Resend -->
      <div class="resend-row">
        <span v-if="resendCooldown > 0" class="resend-timer">
          Resend in {{ resendCooldown }}s
        </span>
        <span
          v-else-if="resendLimitReached"
          class="resend-limit"
        >
          Maximum resend attempts reached.
        </span>
        <a
          v-else
          class="resend-link"
          @click="resendOtp"
        >
          Didn't receive it? Resend code
        </a>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CheckEmail',

  data() {
    return {
      email:            '',
      registrationId:   null,
      digits:           ['', '', '', '', '', ''],
      loading:          false,
      errorMessage:     '',
      resendCooldown:   0,
      resendLimitReached: false,
      cooldownTimer:    null,
    };
  },

  computed: {
    otp() {
      return this.digits.join('');
    },
    isComplete() {
      return this.digits.every((d) => d !== '');
    },
  },

  created() {
    this.email          = sessionStorage.getItem('reg_email') || '';
    this.registrationId = sessionStorage.getItem('reg_registrationId') || null;
  },

  beforeDestroy() {
    if (this.cooldownTimer) clearInterval(this.cooldownTimer);
  },

  methods: {
    // ---- OTP input handling ----

    onInput(idx, event) {
      const val = event.target.value.replace(/\D/g, '');
      this.$set(this.digits, idx, val ? val[0] : '');
      this.errorMessage = '';

      if (val && idx < 5) {
        this.$nextTick(() => this.$refs[`otpInput${idx + 1}`][0].focus());
      }
    },

    onKeydown(idx, event) {
      if (event.key === 'Backspace' && !this.digits[idx] && idx > 0) {
        this.$nextTick(() => this.$refs[`otpInput${idx - 1}`][0].focus());
      }
    },

    onPaste(event) {
      event.preventDefault();
      const pasted = (event.clipboardData || window.clipboardData)
        .getData('text')
        .replace(/\D/g, '')
        .slice(0, 6);

      pasted.split('').forEach((char, i) => {
        this.$set(this.digits, i, char);
      });

      const lastFilled = Math.min(pasted.length, 5);
      this.$nextTick(() => this.$refs[`otpInput${lastFilled}`][0].focus());
    },

    // ---- Verify ----

    verifyOtp() {
      if (!this.isComplete) return;
      this.loading      = true;
      this.errorMessage = '';

      const body = { otp: this.otp, registrationId: this.registrationId };

      this.$app
        .huddleLabsServerCall('profile/register/verify-email', body)
        .then((result) => {
          this.loading = false;
          if (result.success) {
            sessionStorage.setItem('reg_sfContactId', result.sfContactId || '');
            this.$router.push('/register/complete');
          } else {
            this.handleVerifyError(result);
          }
        })
        .catch(() => {
          this.loading = false;
          this.errorMessage = 'Something went wrong. Please try again.';
        });
    },

    handleVerifyError(result) {
      if (result.error_code === 'otp_expired') {
        this.errorMessage = 'Your code has expired. Please request a new one.';
        this.digits = ['', '', '', '', '', ''];
      } else if (result.error_code === 'otp_invalid') {
        this.errorMessage = 'Incorrect code. Please try again.';
        this.digits = ['', '', '', '', '', ''];
        this.$nextTick(() => this.$refs['otpInput0'][0].focus());
      } else {
        this.errorMessage = result.message || 'Verification failed.';
      }
    },

    // ---- Resend ----

    resendOtp() {
      if (this.resendCooldown > 0 || this.resendLimitReached) return;

      this.$app
        .huddleLabsServerCall('profile/register/resend-otp', { registrationId: this.registrationId })
        .then((result) => {
          if (result.success) {
            this.errorMessage = '';
            this.digits       = ['', '', '', '', '', ''];
            this.startCooldown(60);
            this.$nextTick(() => this.$refs['otpInput0'][0].focus());
          } else if (result.error_code === 'resend_limit_reached') {
            this.resendLimitReached = true;
            this.errorMessage = result.error_message || 'OTP Resend Limit Reached'
          } else {
            this.errorMessage = result.error_message || 'Could not resend code.';
          }
        })
        .catch(() => {
          this.errorMessage = 'Could not resend code. Please try again.';
        });
    },

    startCooldown(seconds) {
      this.resendCooldown = seconds;
      if (this.cooldownTimer) clearInterval(this.cooldownTimer);
      this.cooldownTimer = setInterval(() => {
        this.resendCooldown -= 1;
        if (this.resendCooldown <= 0) {
          clearInterval(this.cooldownTimer);
          this.cooldownTimer = null;
        }
      }, 1000);
    },
  },
};
</script>

<style scoped>
.otp-wrapper {
  display: flex;
  justify-content: center;
  padding: 2rem 1rem;
  min-height: 100vh;
  background: #f4f4f0;
}

.otp-card {
  width: 100%;
  max-width: 460px;
  padding: 2rem;
  text-align: center;
  border-radius: 12px;
  align-self: flex-start;
  margin-top: 2rem;
}

.image-wrap {
  margin: 0 auto 1.25rem;
  width: 200px;
}
.image-wrap img { width: 100%; }

.icon-wrap {
  display: flex;
  justify-content: center;
  margin-bottom: 0.75rem;
}

.check-circle {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #EAF3DE;
  border: 2px solid #639922;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.otp-title {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 0.35rem;
}

.otp-subtitle {
  font-size: 0.85rem;
  color: #666660;
  margin-bottom: 1.25rem;
  line-height: 1.6;
}

.otp-boxes {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: 0.5rem;
}

.otp-box {
  width: 42px;
  height: 50px;
  border: 1px solid rgba(0,0,0,0.22);
  border-radius: 8px;
  font-size: 1.4rem;
  font-weight: 500;
  text-align: center;
  outline: none;
  background: #fff;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.otp-box:focus {
  border-color: #185FA5;
  box-shadow: 0 0 0 3px rgba(24,95,165,0.18);
}

.otp-box.filled {
  border-color: #185FA5;
  background: #E6F1FB;
  color: #0C447C;
}

.otp-error {
  color: #cc0f35;
  font-size: 0.82rem;
  margin: 0.5rem 0 0;
}

.resend-row {
  margin-top: 1rem;
  font-size: 0.82rem;
  color: #666660;
}

.resend-link {
  color: #185FA5;
  cursor: pointer;
  text-decoration: underline;
}

.resend-timer,
.resend-limit {
  color: #999992;
}
</style>
